import { useScienceTutorSection } from './scienceTutorContext';
import { Calculator, ListChecks } from 'lucide-react';
import { useState } from 'react';

import {
  QUANTITY_LABELS,
  QUANTITY_UNITS,
  checkAcidBaseAnswer,
  parseNumericAnswer,
  solveAcidBase,
  weakSpecies,
  type AcidBaseCheck,
} from './acidBaseCalculations';
import { PH_PROBLEMS, WEAK_PROBLEMS } from './acidBaseCalculationsData';
import {
  AcidBaseFeedback,
  IonBars,
  IonisationGrid,
  PhStrip,
  StrengthLadder,
} from './AcidBaseCalculationsVisuals';

export function AcidBaseCalculationsProblems({ mode }: { mode: 'ph' | 'weak' }) {
  const problems = mode === 'ph' ? PH_PROBLEMS : WEAK_PROBLEMS;
  const [problemId, setProblemId] = useState(problems[0].id);
  const [answer, setAnswer] = useState('');
  useScienceTutorSection('AcidBaseCalculationsProblems', { problemId, answer });
  const [check, setCheck] = useState<AcidBaseCheck | null>(null);
  const problem = problems.find((candidate) => candidate.id === problemId) ?? problems[0];
  const solved = solveAcidBase(problem.spec);
  const model = solved.value;
  const revealed = check !== null && check.status !== 'invalid';
  const species = 'speciesId' in problem.spec ? weakSpecies(problem.spec.speciesId) : undefined;
  const learnerValue = parseNumericAnswer(answer);
  const learnerPH = model?.quantity === 'pH' && Number.isFinite(learnerValue) ? learnerValue : null;
  const unit = model ? QUANTITY_UNITS[model.quantity] : '';

  const choose = (id: string) => {
    setProblemId(id);
    setAnswer('');
    setCheck(null);
  };

  return (
    <section
      aria-label="Acid–base problems"
      className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-wash-sunshine text-ink">
          <ListChecks className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
            Predict, calculate, then check
          </p>
          <h2 className="text-xl font-extrabold text-ink">{problems.length} reviewed problems</h2>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Choose a problem">
        {problems.map((candidate, index) => (
          <button
            key={candidate.id}
            type="button"
            aria-pressed={candidate.id === problem.id}
            onClick={() => choose(candidate.id)}
            title={candidate.title}
            className={`rounded-full border px-3 py-1.5 text-sm font-extrabold transition-colors motion-reduce:transition-none ${
              candidate.id === problem.id
                ? 'border-brand-sky bg-wash-sky text-ink'
                : 'border-hairline bg-white text-slate2 hover:border-brand-sky/40'
            }`}
          >
            {index + 1}
            <span className="sr-only">. {candidate.title}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-4">
          <div>
            <h3 className="text-lg font-extrabold text-ink">{problem.title}</h3>
            <p className="mt-2 font-medium leading-7 text-ink-soft">{problem.context}</p>
          </div>
          {model ? (
            <>
              <label htmlFor="acid-base-answer" className="block text-sm font-extrabold text-ink">
                Your answer for {QUANTITY_LABELS[model.quantity]}
                {unit ? ` (${unit})` : ''}
              </label>
              <div className="flex gap-2">
                <input
                  id="acid-base-answer"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder={model.quantity === 'pH' ? 'e.g. 2.87' : 'e.g. 1.3e-3'}
                  value={answer}
                  onChange={(event) => {
                    setAnswer(event.target.value);
                    setCheck(null);
                  }}
                  className="min-w-0 flex-1 rounded-xl border border-hairline bg-surface px-4 py-3 font-bold text-ink focus:border-brand-sky focus:outline-none focus:ring-4 focus:ring-brand-sky/15"
                />
                {unit ? (
                  <span className="grid place-items-center rounded-xl bg-surface px-3 text-sm font-extrabold text-slate2">
                    {unit}
                  </span>
                ) : null}
              </div>
              <p className="text-xs font-medium text-slate2">
                Small numbers can be typed as 1.3e-3 or 1.3 × 10^-3. pH answers are marked to two
                decimal places.
              </p>
              <button
                type="button"
                disabled={!answer.trim()}
                onClick={() => setCheck(checkAcidBaseAnswer(problem, answer))}
                className="rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Check answer
              </button>
              {check ? <AcidBaseFeedback check={check} working={model.working} /> : null}
            </>
          ) : (
            <p role="alert" className="rounded-2xl bg-wash-sunshine p-4 text-sm font-bold text-ink">
              {solved.error}
            </p>
          )}
        </div>

        <div className="min-w-0 space-y-5 rounded-[1.5rem] border border-hairline bg-surface p-4 sm:p-5">
          <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
            <Calculator className="h-4 w-4" aria-hidden="true" /> Model view
          </p>
          {revealed && model ? (
            <>
              {model.modelPH !== null ? (
                <>
                  <PhStrip modelPH={model.modelPH} learnerPH={learnerPH} />
                  <IonBars modelPH={model.modelPH} />
                </>
              ) : null}
              {model.modelPercent !== null && species ? (
                <IonisationGrid percent={model.modelPercent} formula={species.formula} />
              ) : null}
              {model.approximation ? (
                <p className="rounded-2xl bg-white px-3 py-2 text-xs font-bold text-ink-soft">
                  5 % rule: the shortcut predicts{' '}
                  {model.approximation.approximatePercent.toFixed(1)} % ionisation, so it is{' '}
                  {model.approximation.valid ? 'acceptable' : 'not acceptable'} here.
                </p>
              ) : null}
            </>
          ) : (
            <p className="rounded-2xl border-2 border-dashed border-hairline px-4 py-6 text-center text-sm font-bold text-slate2">
              Check an answer to reveal the model pH strip and ion bars.
            </p>
          )}
          {mode === 'weak' && revealed ? (
            <div>
              <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.14em] text-slate2">
                Reviewed Ka values, 25 °C
              </p>
              <StrengthLadder highlightId={species?.type === 'acid' ? species.id : null} />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
